import { Picture } from "@/components/media/Picture";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Play } from "lucide-react";
import { useState } from "react";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { heroFor } from "@/data/heroes";
import {
  CRUSADE_COUNT,
  CRUSADE_HISTORY,
  HIGHLIGHT_VIDEOS,
  UPCOMING_CRUSADES,
} from "@/data/crusades";

const TITLE = "Crusades — Light of Life International";
const DESCRIPTION =
  "Upcoming and past Light of Life International gospel crusades across Latin America, the Caribbean and Africa.";

export const Route = createFileRoute("/crusades")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/crusades" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/crusades" }],
  }),
  component: CrusadesPage,
});

const INTRO_POINTS = [
  "Share the love & compassion of Jesus to the poor, hurting and forgotten",
  "Experience the power of God in crusade ministry",
  "Minister in the local churches as part of a Ministry Team",
  "Be a part of impacting and bringing transformation to a city and nation",
];

const GALLERY = [
  "/media/vm04.webp",
  "/media/vm05.webp",
  "/media/vm06.webp",
  "/media/vm07.webp",
  "/media/vm08.webp",
  "/media/vm01.webp",
  "/media/vm02.webp",
];

function FeatureVideo() {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <iframe
        title="61 Crusades. Countless Souls. One Glorious Savior."
        src="https://www.youtube-nocookie.com/embed/bXICQEfVPOE?autoplay=1"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        className="aspect-video w-full rounded-xl"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative block aspect-video w-full overflow-hidden rounded-xl"
    >
      <Picture
        src="/media/loli-video-thumbnail.png"
        alt="Watch: crusade ministry highlights from Light of Life International"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        sizes="100vw"
      />
      <span className="absolute inset-0 bg-navy/35 transition group-hover:bg-navy/20" aria-hidden />
      <span className="relative flex h-full items-center justify-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-white/80">
          <Play className="h-8 w-8 translate-x-[2px] text-white" fill="currentColor" aria-hidden />
        </span>
      </span>
      <span className="sr-only">Play the Light of Life International crusade video</span>
    </button>
  );
}

function CrusadesPage() {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay />

      <main id="main" className="flex-1">
        <PageHero
          title="Crusades"
          image={heroFor("/crusades").image}
          subtitle="Join us for a week with eternal importance… one you will never forget"
        />

        {/* Intro + feature video */}
        <section className="relative -mt-10 overflow-hidden rounded-t-[2.5rem] bg-background">
          <Picture
            src="/media/mcs-bg-christ.jpeg"
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-15"
            sizes="100vw"
          />
          <div className="container-site relative section-y grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow text-sm text-brand-foreground">
                {CRUSADE_COUNT} Crusades in 21 Years
              </p>
              <h2 className="mt-3 text-4xl text-navy sm:text-5xl">Crusades</h2>
              <p className="mt-4 font-heading text-lg uppercase text-foreground">
                Join us for a week with eternal importance… one you will never forget!
              </p>
              <ul className="mt-6 space-y-3">
                {INTRO_POINTS.map((point) => (
                  <li key={point} className="flex gap-3 text-foreground">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-accent" aria-hidden />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <FeatureVideo />
          </div>
        </section>

        {/* Upcoming crusades */}
        <section className="section-y rounded-t-[2.5rem] bg-muted">
          <div className="container-site">
            <h2 className="text-center text-4xl uppercase text-brand-foreground sm:text-5xl">
              Our Upcoming Crusades
            </h2>
            <ul className="mt-14 grid justify-center gap-8 sm:grid-cols-2">
              {UPCOMING_CRUSADES.map((crusade) => {
                const inner = (
                  <>
                    <Picture
                      src={crusade.image}
                      alt={`Crowd worshipping at a Light of Life International crusade in ${crusade.city}, ${crusade.country}`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover"
                      sizes="100vw"
                    />
                    <span className="absolute inset-0 bg-navy/45" aria-hidden />
                    <span className="relative flex h-full flex-col items-center justify-center px-6 text-center">
                      <span className="block text-4xl leading-none uppercase text-white sm:text-5xl">
                        {crusade.city}
                      </span>
                      <span className="mt-1 block text-5xl leading-none uppercase text-white sm:text-6xl">
                        {crusade.country}
                      </span>
                      <span className="eyebrow mt-4 block text-sm text-white">{crusade.dates}</span>
                      <span className="mt-6 inline-block rounded-md border-2 border-white px-6 py-2 font-sans font-semibold text-white transition hover:bg-white hover:text-navy">
                        Sign Up
                      </span>
                    </span>
                  </>
                );
                const cardClass =
                  "relative block aspect-square w-full overflow-hidden rounded-xl mx-auto max-w-md";
                return (
                  <li key={crusade.city}>
                    {crusade.slug ? (
                      <Link
                        to="/crusade/$slug"
                        params={{ slug: crusade.slug }}
                        className={cardClass}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <Link to="/join-us" hash="jointeam" className={cardClass}>
                        {inner}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Destiny band */}
        <section className="relative overflow-hidden bg-navy py-28">
          <Picture
            src="/media/crusades-loop-background-updated.jpg"
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-20"
            sizes="100vw"
          />
          <div className="container-site relative max-w-4xl text-center">
            <h2 className="text-4xl text-white sm:text-5xl">
              God has a beautiful destiny for everyone, and that includes you.
            </h2>
          </div>
        </section>

        {/* Crusade history */}
        <section className="section-y -mt-10 rounded-t-[2.5rem] bg-muted">
          <div className="container-site">
            <h2 className="text-center text-4xl text-navy sm:text-5xl">
              <span className="font-heading italic">{CRUSADE_COUNT}</span> Crusades
            </h2>
            <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {CRUSADE_HISTORY.map((entry) => (
                <li key={entry.year} className="rounded-xl bg-background px-6 py-6 shadow-sm">
                  <h3 className="border-b border-border pb-3 text-3xl text-muted-foreground">
                    {entry.year}
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {entry.cities.map((city) => (
                      <li key={city} className="flex gap-2 font-sans text-base text-foreground">
                        <CheckCircle2
                          className="mt-1 h-4 w-4 shrink-0 text-brand-foreground"
                          aria-hidden
                        />
                        <span>{city}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Highlight videos */}
        <section className="section-y rounded-t-[2.5rem] bg-[#2f2f2f]">
          <div className="container-site">
            <h2 className="text-center text-4xl text-white sm:text-5xl">
              <span className="font-sans italic font-light">Crusade</span> Highlight Videos
            </h2>
            <ul className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {HIGHLIGHT_VIDEOS.map((video) => (
                <li key={video.id}>
                  <a
                    href={`https://www.youtube.com/watch?v=${video.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="group block"
                  >
                    <span className="relative block aspect-video overflow-hidden rounded-lg">
                      <Picture
                        src={video.thumb}
                        alt={`${video.title} crusade highlight video`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover opacity-70 transition group-hover:opacity-90"
                        sizes="100vw"
                      />
                      <span className="relative flex h-full items-center justify-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-accent">
                          <Play
                            className="h-6 w-6 translate-x-[2px] text-accent"
                            fill="currentColor"
                            aria-hidden
                          />
                        </span>
                      </span>
                    </span>
                    <span className="eyebrow mt-4 block text-center text-sm text-white/85">
                      {video.title}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Fire Carriers promo */}
        <section className="relative overflow-hidden bg-gradient-to-r from-[#c8531f] via-[#8b3b6b] to-[#3d3b8c] py-20">
          <div className="container-site relative grid items-center gap-10 md:grid-cols-[1fr_auto]">
            <div>
              <h2 className="text-4xl text-white sm:text-5xl">
                <span className="text-accent">You Shall Receive Power!</span> What Happens When
                Average People Dare To Dream with an Extraordinary God?
              </h2>
              <p className="mt-4 text-white">Fire Carriers is out now! Pick up your copy today!</p>
              <Link
                to="/fire-carriers"
                className="mt-8 inline-block rounded-md bg-navy px-8 py-4 font-sans font-semibold text-white transition hover:bg-navy/90"
              >
                Order Now
              </Link>
            </div>
            <img
              src="/media/FC_transparent_3D_large-218x300-1.webp"
              alt="Fire Carriers book by Stephen Evans"
              loading="lazy"
              decoding="async"
              className="mx-auto w-48 md:w-56"
            />
          </div>
        </section>

        {/* Fire Starters and Rain Crusaders promo */}
        <section className="section-y bg-background">
          <div className="container-site grid items-center gap-12 md:grid-cols-2">
            <img
              src="/media/FSRC_3D_transparent-250x300-1.webp"
              alt="Fire Starters and Rain Crusaders book by Stephen Evans"
              loading="lazy"
              decoding="async"
              className="mx-auto w-56 md:w-72"
            />
            <div>
              <h2 className="text-4xl text-muted-foreground sm:text-5xl">
                Read more about the crusades, miracles, and testimonies of{" "}
                <span className="text-brand-ink">Light of Life International.</span>
              </h2>
              <p className="mt-4 text-foreground">Pick up your copy today!</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/fire-starters-and-rain-crusaders"
                  className="rounded-md bg-navy px-8 py-4 font-sans font-semibold text-white transition hover:bg-navy/90"
                >
                  Order Now
                </Link>
                <Link
                  to="/fire-starters-and-rain-crusaders"
                  className="rounded-md border-2 border-navy px-8 py-4 font-sans font-semibold text-navy transition hover:bg-navy hover:text-white"
                >
                  About Fire Starters &amp; Rain Crusaders
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Join us CTA + gallery */}
        <section className="section-y rounded-t-[2.5rem] bg-muted">
          <div className="container-site grid items-center gap-10 md:grid-cols-2">
            <h2 className="text-4xl text-muted-foreground sm:text-5xl">
              Join Us On Our Next Crusade!
            </h2>
            <div>
              <p className="text-foreground">
                If you are interested in joining us on an upcoming crusade, joining our team of
                global intercessors, or want more information on LOLI, please contact us. God bless
                you!
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-block rounded-md bg-accent px-8 py-4 font-sans font-semibold text-navy transition hover:brightness-110"
              >
                Connect With Us
              </Link>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 px-3 sm:grid-cols-4 lg:grid-cols-7">
            {GALLERY.map((src) => (
              <Picture
                key={src}
                src={src}
                alt="Light of Life International crusade crowd"
                loading="lazy"
                decoding="async"
                className="h-56 w-full rounded-md object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
                widths={[640, 1024]}
              />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
